<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
			<h2 class="gh-intro__title">Freshbooks</h2>
			<p class="gh-intro__desc">
				Freshbooks API credentials, invoice fetching and Client ID synchronization.
			</p>
			<div class="mt-3">
				<button class="gh-save-btn" :disabled="fetchInvoicesSync.inProgress" @click="fetchInvoices()">
					<font-awesome-icon v-if="fetchInvoicesSync.inProgress" :icon="['fas', 'spinner']" class="fa-spin" />
					<font-awesome-icon v-else :icon="['fas', 'file-invoice-dollar']" />
					Fetch Invoices
				</button>
			</div>
		</aside>

		<section class="gh-accounts">
			<div class="ada-card">
			<form v-if="settings" @submit.prevent="saveSettings">
				<div class="gh-section">
					<div class="gh-section__title">API Credentials</div>
					<div class="gh-field">
						<label>Client ID</label>
						<input type="text" class="form-control" v-model="settings.clientId" />
					</div>
					<div class="gh-field">
						<label>Client Secret</label>
						<input type="text" class="form-control" v-model="settings.clientSecret" />
					</div>
					<div class="gh-field">
						<label>Account ID</label>
						<input type="text" class="form-control" v-model="settings.accountId" />
					</div>
					<div class="gh-field">
						<label>Redirect Url</label>
						<input type="text" class="form-control" v-model="settings.redirectUrl" />
					</div>
				</div>

				<div class="gh-section">
					<div class="gh-section__title">OAuth Tokens <span class="gh-section__hint">read-only</span></div>
					<div class="gh-field">
						<label>Code</label>
						<input type="text" class="form-control" v-model="settings.code" readonly />
					</div>
					<div class="gh-field">
						<label>Access Token</label>
						<input type="textarea" class="form-control" v-model="settings.accessToken" readonly />
					</div>
					<div class="gh-field">
						<label>Refresh Token</label>
						<input type="textarea" class="form-control" v-model="settings.refreshToken" readonly />
					</div>
				</div>
			</form>
			<div class="gh-section border-top pt-3 mt-3">
				<div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
					<div>
						<div class="gh-section__title mb-1">Fetch Invoices</div>
						<div class="gh-help mt-0">
							Sinhronizuje Freshbooks invoices samo za klijente koji imaju Freshbooks Client ID.
						</div>
					</div>
					<div class="text-muted small" v-if="fetchInvoicesSync.inProgress">
						{{ fetchInvoicesSync.currentIndex }} / {{ fetchInvoicesSync.queue.length }}
					</div>
				</div>

				<div v-if="fetchInvoicesSync.inProgress" class="mb-3">
					<div class="progress" style="height: 8px">
						<div
							class="progress-bar bg-primary"
							role="progressbar"
							:style="{ width: fetchInvoicesProgress + '%' }"
							:aria-valuenow="fetchInvoicesProgress"
							aria-valuemin="0"
							aria-valuemax="100"
						></div>
					</div>
					<p class="mb-0 mt-2" v-if="fetchInvoicesSync.lastCompleted">
						Završeno: <strong>{{ fetchInvoicesSync.lastCompleted.name }}</strong>
						<span class="text-muted">(#{{ fetchInvoicesSync.lastCompleted.id }})</span>
						<span class="badge bg-light text-dark ms-2">
							Invoices: {{ fetchInvoicesSync.lastCompleted.stats.total }}
						</span>
					</p>
				</div>

				<div v-if="fetchInvoicesSync.logs.length" class="mb-3">
					<h6 class="text-uppercase small text-muted mb-2">Rezultati</h6>
					<div class="list-group small shadow-sm">
						<div
							class="list-group-item d-flex justify-content-between align-items-center"
							v-for="log in fetchInvoicesSync.logs"
							:key="`invoice-log-${log.clientId}-${log.timestamp}`"
						>
							<div>
								<strong>{{ log.name }}</strong>
								<span class="text-muted ms-2">#{{ log.clientId }}</span>
								<span class="badge bg-light text-dark ms-2">
									Invoices: {{ log.stats.total }}
								</span>
							</div>
							<span
								class="badge"
								:class="{
									'bg-success': log.status === 'done',
									'bg-danger': log.status === 'error',
								}"
							>
								{{ log.status === 'done' ? 'Završeno' : 'Greška' }}
							</span>
						</div>
					</div>
				</div>
			</div>
			<div class="gh-section border-top pt-3 mt-3">
				<div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
					<div>
						<div class="gh-section__title mb-1">Freshbooks Client ID Sync</div>
						<div class="gh-help mt-0">
							Automatski pronađi Freshbooks Client ID vrednosti na osnovu emailova kontakata.
						</div>
					</div>
					<div class="d-flex gap-2 align-items-center flex-wrap">
						<div class="form-check form-switch my-auto">
							<input
								class="form-check-input"
								type="checkbox"
								id="syncMissingOnly"
								v-model="clientIdSync.options.missingOnly"
								:disabled="clientIdSync.inProgress || clientIdSync.isLoadingClients"
							/>
							<label class="form-check-label small" for="syncMissingOnly">
								Samo klijenti bez ID-a
							</label>
						</div>
						<button
							class="gh-save-btn"
							:disabled="clientIdSync.inProgress || clientIdSync.isLoadingClients"
							@click="startClientIdSync"
						>
							<font-awesome-icon
								v-if="clientIdSync.inProgress || clientIdSync.isLoadingClients"
								:icon="['fas', 'spinner']"
								class="fa-spin"
							/>
							<font-awesome-icon v-else :icon="['fas', 'sync']" />
							{{ clientIdSync.inProgress ? 'Sinhronizacija u toku...' : 'Pokreni sinhronizaciju' }}
						</button>
					</div>
				</div>

				<div v-if="clientIdSync.inProgress" class="mb-3">
					<div class="d-flex justify-content-between align-items-center mb-1">
						<strong>Napredak</strong>
						<small>{{ clientIdSync.currentIndex }} / {{ clientIdSync.queue.length }}</small>
					</div>
					<div class="progress" style="height: 8px">
						<div
							class="progress-bar bg-success"
							role="progressbar"
							:style="{ width: syncClientIdProgress + '%' }"
							:aria-valuenow="syncClientIdProgress"
							aria-valuemin="0"
							aria-valuemax="100"
						></div>
					</div>
					<p class="mb-0 mt-2" v-if="clientIdSync.currentClient">
						Trenutno: <strong>{{ clientIdSync.currentClient.name }}</strong>
						<span class="text-muted">(#{{ clientIdSync.currentClient.id }})</span>
					</p>
				</div>

				<div v-if="clientIdSync.logs.length" class="mb-3">
					<h6 class="text-uppercase small text-muted mb-2">Rezultati</h6>
					<div class="list-group small shadow-sm">
						<div
							class="list-group-item d-flex justify-content-between align-items-center"
							v-for="log in clientIdSync.logs"
							:key="`log-${log.clientId}-${log.status}-${log.freshbooksId}-${log.timestamp}`"
						>
							<div>
								<strong>{{ log.name }}</strong>
								<span class="text-muted ms-2">#{{ log.clientId }}</span>
								<span v-if="log.freshbooksId" class="badge bg-light text-dark ms-2">
									FB {{ log.freshbooksId }}
								</span>
								<div v-if="log.message" class="text-danger mt-1">{{ log.message }}</div>
							</div>
							<span
								class="badge"
								:class="{
									'bg-success': log.status === 'found',
									'bg-warning text-dark': log.status === 'not_found',
									'bg-danger': log.status === 'error',
								}"
							>
								{{ statusLabel(log.status) }}
							</span>
						</div>
					</div>
				</div>

				<div v-if="clientIdSync.completed">
					<div v-if="clientIdSync.notFound.length" class="alert alert-warning">
						<h6 class="mb-1">Klijenti bez Freshbooks ID-a</h6>
						<ul class="mb-0 ps-3">
							<li v-for="missing in clientIdSync.notFound" :key="`missing-${missing.id}`">
								<strong>{{ missing.name }}</strong> (ID: {{ missing.id }})
							</li>
						</ul>
					</div>
					<div v-else class="alert alert-success mb-0">
						Svi klijenti imaju upisan Freshbooks ID 🎉
					</div>
				</div>
			</div>
				</div>
			</section>
		</div>

		<hr class="gh-divider" />

		<div class="gh-save-row">
			<button v-if="settings" class="gh-save-btn" @click="saveSettings">
				<i class="fas fa-save"></i>
				Save changes
			</button>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
export default {
	name: 'FreshbooksSettings',
	data() {
		return {
			loadingFetch: false,
			name: 'freshbooksSettings',
			type: 'settings',
			settings: {
				clientId: '',
				clientSecret: '',
				accountId: '',
				redirectUrl: '',
				code: '',
				accessToken: '',
				refreshToken: '',
			},
			clientIdSync: {
				inProgress: false,
				isLoadingClients: false,
				queue: [],
				currentIndex: 0,
				currentClient: null,
				logs: [],
				notFound: [],
				completed: false,
				options: {
					missingOnly: true,
				},
			},
			fetchInvoicesSync: {
				inProgress: false,
				isLoadingClients: false,
				queue: [],
				currentIndex: 0,
				lastCompleted: null,
				logs: [],
			},
		}
	},
	computed: {
		syncClientIdProgress() {
			if (!this.clientIdSync.queue.length) {
				return 0
			}
			const percentage =
				(this.clientIdSync.currentIndex / this.clientIdSync.queue.length) * 100
			return Math.min(100, Math.round(percentage))
		},
		fetchInvoicesProgress() {
			if (!this.fetchInvoicesSync.queue.length) {
				return 0
			}
			const percentage =
				(this.fetchInvoicesSync.currentIndex / this.fetchInvoicesSync.queue.length) * 100
			return Math.min(100, Math.round(percentage))
		},
	},
	created() {
		this.getSettings()
	},
	methods: {
		getSettings() {
			restApi
				.get('/settings', { params: { name: this.name, type: this.type } })
				.then(({ data }) => {
					if (data) {
						this.settings = JSON.parse(data.data)
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		saveSettings() {
			restApi
				.post('/settings', {
					name: this.name,
					type: this.type,
					data: JSON.stringify(this.settings),
				})
				.then(({ data }) => {
					data.data = JSON.parse(data.data)
					this.settings = data.data
					this.$swal.fire({
						icon: 'success',
						title: 'Settings successfully saved!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Fetch Invoices
		fetchInvoices() {
			if (this.fetchInvoicesSync.inProgress) {
				return
			}

			this.loadingFetch = true
			this.fetchInvoicesSync.inProgress = true
			this.fetchInvoicesSync.isLoadingClients = true
			this.fetchInvoicesSync.queue = []
			this.fetchInvoicesSync.currentIndex = 0
			this.fetchInvoicesSync.lastCompleted = null
			this.fetchInvoicesSync.logs = []

			restApi
				.get('/freshbooks/client-ids/clients', { params: { missing_only: false } })
				.then(({ data }) => {
					const clients = (data.clients || []).filter(client => client.freshbooks_client_id)
					this.fetchInvoicesSync.queue = clients

					if (!clients.length) {
						this.$swal.fire({
							icon: 'info',
							title: 'Nema klijenata sa Freshbooks ID-om',
							timer: 4000,
						})
						this.fetchInvoicesSync.inProgress = false
						return
					}

					this.processNextInvoiceFetch()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Neuspešno učitavanje liste klijenata'),
						timer: 6000,
					})
					this.fetchInvoicesSync.inProgress = false
				})
				.finally(() => {
					this.fetchInvoicesSync.isLoadingClients = false
					this.loadingFetch = false
				})
		},
		processNextInvoiceFetch() {
			if (!this.fetchInvoicesSync.inProgress) {
				return
			}

			if (this.fetchInvoicesSync.currentIndex >= this.fetchInvoicesSync.queue.length) {
				this.finishInvoiceFetch()
				return
			}

			const client = this.fetchInvoicesSync.queue[this.fetchInvoicesSync.currentIndex]

			restApi
				.get(`/freshbooks/invoices/fetch/${client.id}`)
				.then(({ data }) => {
					const stats = data?.stats || { total: 0 }
					const entry = {
						clientId: client.id,
						name: client.name,
						stats,
						status: 'done',
						timestamp: Date.now(),
					}
					this.fetchInvoicesSync.lastCompleted = {
						id: client.id,
						name: client.name,
						stats,
					}
					this.fetchInvoicesSync.logs.unshift(entry)
				})
				.catch(error => {
					const entry = {
						clientId: client.id,
						name: client.name,
						stats: { total: 0 },
						status: 'error',
						message: handleErrors(error, ''),
						timestamp: Date.now(),
					}
					this.fetchInvoicesSync.logs.unshift(entry)
				})
				.finally(() => {
					this.fetchInvoicesSync.currentIndex++
					this.processNextInvoiceFetch()
				})
		},
		finishInvoiceFetch() {
			this.fetchInvoicesSync.inProgress = false
			this.fetchInvoicesSync.isLoadingClients = false
			this.$swal.fire({
				icon: 'success',
				title: 'Fetch Invoices završen',
				timer: 4000,
			})
		},
		startClientIdSync() {
			if (this.clientIdSync.inProgress) {
				return
			}

			this.clientIdSync.inProgress = true
			this.clientIdSync.isLoadingClients = true
			this.clientIdSync.queue = []
			this.clientIdSync.currentIndex = 0
			this.clientIdSync.currentClient = null
			this.clientIdSync.logs = []
			this.clientIdSync.notFound = []
			this.clientIdSync.completed = false

			restApi
				.get('/freshbooks/client-ids/clients', {
					params: { missing_only: this.clientIdSync.options.missingOnly },
				})
				.then(({ data }) => {
					this.clientIdSync.queue = data.clients || []

					if (!this.clientIdSync.queue.length) {
						this.$swal.fire({
							icon: 'info',
							title: 'Nema klijenata za sinhronizaciju',
							timer: 4000,
						})
						this.clientIdSync.inProgress = false
						this.clientIdSync.completed = true
						return
					}

					this.processNextClientId()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Neuspešno učitavanje liste klijenata'),
						timer: 6000,
					})
					this.clientIdSync.inProgress = false
				})
				.finally(() => {
					this.clientIdSync.isLoadingClients = false
				})
		},
		processNextClientId() {
			if (!this.clientIdSync.inProgress) {
				return
			}

			if (this.clientIdSync.currentIndex >= this.clientIdSync.queue.length) {
				this.finishClientIdSync()
				return
			}

			const client = this.clientIdSync.queue[this.clientIdSync.currentIndex]
			this.clientIdSync.currentClient = client

			restApi
				.post('/freshbooks/client-ids/sync-client', {
					client_id: client.id,
				})
				.then(({ data }) => {
					const entry = {
						clientId: client.id,
						name: client.name,
						status: data.status,
						freshbooksId: data.freshbooks_client_id,
						timestamp: Date.now(),
					}

					if (data.status === 'found' && data.freshbooks_client_id) {
						this.clientIdSync.queue[this.clientIdSync.currentIndex].freshbooks_client_id =
							data.freshbooks_client_id
					} else {
						this.clientIdSync.notFound.push({ id: client.id, name: client.name })
					}

					this.clientIdSync.logs.unshift(entry)
				})
				.catch(error => {
					const entry = {
						clientId: client.id,
						name: client.name,
						status: 'error',
						message: handleErrors(error, ''),
						timestamp: Date.now(),
					}
					this.clientIdSync.logs.unshift(entry)
					this.clientIdSync.notFound.push({ id: client.id, name: client.name })
				})
				.finally(() => {
					this.clientIdSync.currentIndex++
					this.processNextClientId()
				})
		},
		finishClientIdSync() {
			const total = this.clientIdSync.queue.length
			const missing = this.clientIdSync.notFound.length
			const found = total - missing

			this.clientIdSync.inProgress = false
			this.clientIdSync.currentClient = null
			this.clientIdSync.completed = true

			if (total > 0) {
				this.$swal.fire({
					icon: 'success',
					title: 'Sinhronizacija završena',
					html: `<div class="text-start"><strong>Pronađeno:</strong> ${found}<br/><strong>Bez ID-a:</strong> ${missing}</div>`,
					timer: 6000,
				})
			}
		},
		statusLabel(status) {
			if (status === 'found') {
				return 'Pronađen'
			}
			if (status === 'not_found') {
				return 'Nije pronađen'
			}
			if (status === 'error') {
				return 'Greška'
			}
			return status
		},
	},
}
</script>
